import { TFunction } from 'i18next';

import { amountWithCurrencySymbol, mapCurrencySymbol } from './currency';
import { UserStatus } from '@/api/auth';
import { JackpotLevel } from '@/api/common';

export interface StaticPool {
  title: string;
  minBet: number;
  image: string;
  type: Levels;
  hitRange: string;
}

export interface Pool extends StaticPool {
  prize: string;
}

export const PIONEERS_LOWEST_HIT_RANGE = 10000;
export const LEGENDS_LOWEST_HIT_RANGE = 100000;
export const TITANS_LOWEST_HIT_RANGE = 1000000;

export enum Levels {
  PIONEERS = 'level_3',
  LEGENDS = 'level_2',
  TITANS = 'level_1',
}

export const mapLevels: Record<Levels, { title: string; icon: string }> = {
  [Levels.PIONEERS]: {
    title: 'shortTypes.pioneers',
    icon: '/jackpot/levels/pioneersIcon.png',
  },
  [Levels.LEGENDS]: {
    title: 'shortTypes.legends',
    icon: '/jackpot/levels/legendsIcon.png',
  },
  [Levels.TITANS]: {
    title: 'shortTypes.titans',
    icon: '/jackpot/levels/titansIcon.png',
  },
};

export const poolsStaticContent = (t: TFunction): StaticPool[] => [
  {
    title: 'types.pioneers',
    minBet: 1,
    image: '/jackpot/levels/pioneers.png',
    type: Levels.PIONEERS,
    hitRange: t('hitRangePool.pioneers'),
  },
  {
    title: 'types.legends',
    minBet: 5,
    image: '/jackpot/levels/legends.png',
    type: Levels.LEGENDS,
    hitRange: t('hitRangePool.legends'),
  },
  {
    title: 'types.titans',
    minBet: 10,
    image: '/jackpot/levels/titans.png',
    type: Levels.TITANS,
    hitRange: t('hitRangePool.titans'),
  },
];

export const updatePoolsWithPrizes = (
  levels: JackpotLevel[],
  currency: string,
  t: TFunction
): Pool[] =>
  poolsStaticContent(t).reduce((acc: Pool[], pool: StaticPool) => {
    const level = levels.find((lvl) => lvl.identifier === pool.type);
    if (level) {
      const amount = level.amountCents / 100;
      const prizeSum = amount.toLocaleString(
        undefined,
        amount % 1 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {}
      );

      const mask = mapCurrencySymbol[currency] || null;

      acc.push({
        ...pool,
        prize: mask ? mask.replace('{}', prizeSum) : prizeSum,
      });
    }
    return acc;
  }, []);

export const getTotalSumWithCurrency = (
  levels: JackpotLevel[],
  currency: string
): string => {
  const totalPrizeSum = (
    levels.reduce((acc, { amountCents }) => acc + amountCents, 0) / 100
  ).toLocaleString();

  return amountWithCurrencySymbol(currency, totalPrizeSum);
};

export const getTotalJackpotAmountCents = (levels: JackpotLevel[]) =>
  levels.reduce((acc, level) => acc + level.amountCents, 0);

export const TEST_JACKPOTS_STATUS = 'test_jackpots';

export const getIsJackpotsVisibleToUser = (userStatuses: UserStatus[]) =>
  userStatuses.some((status) => status.id === TEST_JACKPOTS_STATUS);
